/**
 * Validation helper functions
 * Provides reusable validation logic across the application
 */

import { REGEX_PATTERNS, VALIDATION_CONSTANTS, FILE_CONSTANTS } from '../constants/appConstants';

/**
 * Validates email address
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  return REGEX_PATTERNS.EMAIL.test(trimmed) && trimmed.length <= VALIDATION_CONSTANTS.EMAIL_MAX_LENGTH;
};

/**
 * Validates phone number (Indian format)
 * @param {string} phone - Phone to validate
 * @returns {boolean} - Whether phone is valid
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const cleaned = phone.replace(/[\s-]/g, '');
  return REGEX_PATTERNS.PHONE_INDIA.test(cleaned);
};

/**
 * Validates name
 * @param {string} name - Name to validate
 * @returns {boolean} - Whether name is valid
 */
export const validateName = (name) => {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  return (
    trimmed.length >= VALIDATION_CONSTANTS.NAME_MIN_LENGTH &&
    trimmed.length <= VALIDATION_CONSTANTS.NAME_MAX_LENGTH &&
    REGEX_PATTERNS.NAME.test(trimmed)
  );
};

/**
 * Validates password
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password || typeof password !== 'string') {
    errors.push('Password is required');
    return { isValid: false, errors };
  }
  
  if (password.length < VALIDATION_CONSTANTS.PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${VALIDATION_CONSTANTS.PASSWORD_MIN_LENGTH} characters`);
  }
  
  if (password.length > VALIDATION_CONSTANTS.PASSWORD_MAX_LENGTH) {
    errors.push(`Password must be at most ${VALIDATION_CONSTANTS.PASSWORD_MAX_LENGTH} characters`);
  }
  
  if (!REGEX_PATTERNS.PASSWORD.test(password)) {
    errors.push('Password must contain at least one letter and one number');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates date of birth and calculates age
 * @param {string} dateOfBirth - Date of birth to validate
 * @returns {Object} - Validation result with isValid, age, and errors
 */
export const validateDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) {
    return { isValid: false, age: null, errors: ['Date of birth is required'] };
  }
  
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, age: null, errors: ['Invalid date format'] };
  }
  
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
    ? age - 1 
    : age;
  
  const errors = [];
  
  if (actualAge < VALIDATION_CONSTANTS.AGE_MIN) {
    errors.push(`Age must be at least ${VALIDATION_CONSTANTS.AGE_MIN} years`);
  }
  
  if (actualAge > VALIDATION_CONSTANTS.AGE_MAX) {
    errors.push(`Age must be at most ${VALIDATION_CONSTANTS.AGE_MAX} years`);
  }
  
  if (birthDate >= today) {
    errors.push('Date of birth must be in the past');
  }
  
  return {
    isValid: errors.length === 0,
    age: actualAge,
    errors,
  };
};

/**
 * Validates URL
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether URL is valid
 */
export const validateURL = (url) => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return (
    REGEX_PATTERNS.URL.test(trimmed) && 
    trimmed.length <= VALIDATION_CONSTANTS.URL_MAX_LENGTH
  );
};

/**
 * Validates file
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Maximum file size in bytes
 * @param {string[]} options.allowedTypes - Allowed MIME types
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = FILE_CONSTANTS.MAX_FILE_SIZE,
    allowedTypes = [],
  } = options;
  
  const errors = [];
  
  if (!file) {
    errors.push('File is required');
    return { isValid: false, errors };
  }
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    errors.push(`File type must be one of: ${allowedTypes.join(', ')}`);
  }
  
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    errors.push(`File size must be less than ${maxSizeMB}MB`);
  }
  
  // Validate filename
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9\-_.]/g, '');
  if (sanitizedFileName.length === 0 || sanitizedFileName.length > VALIDATION_CONSTANTS.MAX_FILENAME_LENGTH) {
    errors.push('Invalid file name');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates number in range
 * @param {any} value - Value to validate
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {boolean} - Whether value is valid
 */
export const validateNumberRange = (value, min, max) => {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Sanitizes string input
 * @param {string} input - Input to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/\0/g, '')
    .replace(/[\x00-\x1F\x7F]/g, '')
    .trim();
};

